import classNames from 'classnames/bind';
import React from 'react';
import { Link, LinkProps } from 'react-router-dom';

import Styles from './button.module.scss';

const cx = classNames.bind(Styles);

/* Accessibility Notes:
  https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role

  aria-haspopup - this shoudl be defined if the button will trigger a popup
  aria-label - this shoudl be defined if the button does not contain text
*/
interface Props
  extends Omit<Partial<HTMLButtonElement>, 'type' | 'disabled' | 'children'>,
    Omit<Partial<React.ForwardRefExoticComponent<LinkProps & React.RefAttributes<HTMLAnchorElement>>>, 'href'> {
  /** Size of the button */
  size?: 'small' | 'large';
  /** Enables full width button */
  fullWidth?: boolean;
  /** Type of button  */
  type?: 'primary' | 'secondary' | 'ternary' | 'warn' | 'link-primary' | 'link-secondary' | 'link-ternary' | 'icon';
  /** Provides the base button onClick event */
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** Disables the button */
  disabled?: boolean;
  /** Child elements, should only contain text and icons */
  children: React.ReactNode;
  /**
   * Class Name for custom stylings for the button
   */
  className?: string;
  /** This shoudl be set when the button will open a popup  */
  'aria-haspopup'?: boolean;
  /** This should be set when the button does not have meaningful text  */
  'aria-label'?: string;
  /** Link to navigate to */
  href?: string;
  target?: string;
  /** If is true and used in conjunction with type 'link', the button will be a React Router Link */
  useReactRouterLink?: boolean;
  htmlFor?: string;
}

const Button = ({
  size = 'small',
  fullWidth = false,
  type = 'primary',
  disabled,
  children,
  className,
  href,
  useReactRouterLink,
  htmlFor,
  ...otherProps
}: Props) => {
  const isLink: boolean = type.startsWith('link');

  const propsToPass: { [key: string]: unknown } = {
    disabled,
    'aria-disabled': disabled,
    className: cx('form-element', 'button', `button-${type}`, `button-${size}`, className || '', {
      'button-fullwidth': fullWidth,
      disabled: disabled && isLink,
    }),
    ...((!isLink && { htmlFor }) || {}),
    ...otherProps,
  };

  if (isLink) {
    if (useReactRouterLink) {
      return (
        <Link role='link' to={!disabled && href ? href : ''} {...propsToPass}>
          {children}
        </Link>
      );
    } else {
      return (
        <a role='link' href={!disabled ? href : ''} {...propsToPass}>
          {children}
        </a>
      );
    }
  }

  return (
    <button role='button' {...propsToPass}>
      {children}
    </button>
  );
};

export default Button;
